/**
 * The release flags this client may be told about, and what each one serves when nothing has told
 * it. The mirror of the backend's flag catalog, and the keys are the same strings: no alias, no
 * environment prefix.
 *
 * A default exists because an off flag is **absent** from `GET /access`, never `false`. Read that
 * absence literally and a client with no answer at all holds zero flags, which silently turns
 * every flag off the moment a fetch fails. A release flag guards something unreleased and is off
 * either way; a kill switch guards something already running, so its safe side is on and losing
 * the answer must leave it there.
 *
 * The default applies only when no answer has arrived. An answer that did arrive is complete: the
 * backend resolved its own defaults into it before publishing, so a key missing from its `flags`
 * is off.
 */

/** The `flags` map of an answer. Only on-keys appear, so a read may find nothing. */
export type FlagMap = Readonly<Record<string, true | undefined>>;

/** What each flag serves when no answer has arrived. A key nobody declared serves off. */
export type FlagDefaults = Readonly<Record<string, boolean | undefined>>;

export const FLAG_DEFAULTS: FlagDefaults = {
  // Epic 1's paywall merges dark: off until the subscription surfaces are ready to charge.
  "subscriptions-paywall": false,
  // Release-shaped: what it guards is unreleased, so off is the safe side.
  "access-probe": false,
  // Kill-switch-shaped, and the only key here with that polarity. Losing the answer must leave an
  // on switch on, and nothing else in this catalog would prove the two sides are read differently.
  "access-probe-kill": true,
  // Epic 3's handles. Release-shaped, so off is the safe side: with no answer the client must not
  // ask for a handle the server would refuse.
  "handles": false,
  // The NIIMBOT D11_H printer. Release-shaped, so off is the safe side: no answer, no D11_H.
  "niimbot-d11h": false,
  // The NIIMBOT B1 Pro's 300 DPI render. Release-shaped, so off is the safe side. A key missing
  // from here AND from the server's answer can never be on, which would make its target
  // permanently unreachable rather than merely dark.
  "niimbot-b1-pro": false,
  // The NIIMBOT D110_M printer. Release-shaped, so off is the safe side: nothing here has met the
  // hardware, so no answer must not offer a target whose every number is still derived.
  "niimbot-d110m": false,
  // Printer setup as its own destination, with a guided first run. Release-shaped, so off is the
  // safe side: with no answer a dealer keeps the printer screen they already know.
  "printer-setup": false,
  "card-hedge-sales": false,
  // The Label settings screen. Release-shaped, so off is the safe side: no answer, no new screen.
  "label-settings": false,
  // The Personal Collection. Named like a release flag so a rollout can be narrowed, but ON when no
  // answer has arrived because it shipped already: losing the answer must not take it from testers.
  "personal-collection": true,
  "buylist": false,
  // Wants, following a Vendor, and the match surfaces they feed. Release-shaped, so off is the
  // safe side: with no answer the client offers no want the server would refuse to hold.
  "wants-and-follows": false,
};

/** Whether an answer that did arrive lists this flag as on. */
export function flagIsListed(flags: FlagMap, key: string): boolean {
  return flags[key] === true;
}

/**
 * Whether one flag is on.
 *
 * `flags` is `null` only when no answer has arrived for this Workspace, and that is the one case
 * `defaults` decides. The two absences are not the same question.
 */
export function flagIsOn(flags: FlagMap | null, key: string, defaults: FlagDefaults): boolean {
  return flags === null ? defaults[key] === true : flagIsListed(flags, key);
}
